<script setup lang="ts">
/**
 * A slider: a dark steel groove with an aged-brass fill up to the value and a
 * brass knob, the kit's one slider (shop amounts, the exchange, XP convert). The native range input stays (keyboard, touch, form semantics);
 * only its track and thumb are painted. The input is 44px tall for the touch
 * target and the groove is drawn inside it.
 *
 * `--fill` is the value as a percent of the range, set from the model, so the
 * groove can fill without a second element.
 */
import { computed } from 'vue';

const props = withDefaults(defineProps<{ min?: number; max?: number; step?: number }>(), { min: 0, max: 100, step: 1 });
const model = defineModel<number>({ required: true });

const fill = computed(() => `${((model.value - props.min) / (props.max - props.min)) * 100}%`);
</script>

<template>
  <input v-model.number="model" type="range" class="slider" :min="min" :max="max" :step="step" :style="{ '--fill': fill }" v-bind="$attrs" />
</template>

<script lang="ts">
export default { inheritAttrs: false };
</script>

<style scoped>
.slider {
  --groove: 10px;
  --knob: 30px;
  width: 100%;
  min-width: 6rem;
  height: 44px;
  margin: 0;
  background: transparent;
  cursor: pointer;
  touch-action: pan-y;
  appearance: none;
  -webkit-appearance: none;
}

.slider:focus {
  outline: none;
}

.slider::-webkit-slider-runnable-track {
  height: var(--groove);
  border-radius: 999px;
  background: linear-gradient(90deg, #b88e44 0 var(--fill), #0b1118 var(--fill) 100%);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(185, 138, 58, 0.5);
}

.slider::-moz-range-track {
  height: var(--groove);
  border-radius: 999px;
  background: linear-gradient(90deg, #b88e44 0 var(--fill), #0b1118 var(--fill) 100%);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(185, 138, 58, 0.5);
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: var(--knob);
  height: var(--knob);
  margin-top: calc((var(--groove) - var(--knob)) / 2);
  border: 0;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #f1dca6, #b88e44 60%, #6f5220);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.6), inset 0 -2px 0 rgba(0, 0, 0, 0.25);
}

.slider::-moz-range-thumb {
  width: var(--knob);
  height: var(--knob);
  border: 0;
  border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #f1dca6, #b88e44 60%, #6f5220);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.6), inset 0 -2px 0 rgba(0, 0, 0, 0.25);
}

.slider:active::-webkit-slider-thumb {
  transform: translateY(2px);
}

.slider:active::-moz-range-thumb {
  transform: translateY(2px);
}

.slider:focus-visible::-webkit-slider-thumb {
  outline: 3px solid #fff;
  outline-offset: 2px;
}

.slider:focus-visible::-moz-range-thumb {
  outline: 3px solid #fff;
  outline-offset: 2px;
}
</style>
